import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { isArtistRole } from '../lib/rolePermissions';
import { validatePassword } from '../lib/passwordValidator';
import { normalizeInstagramUsername } from '../lib/artistWebService';

const AuthContext = createContext(null);

/** Normalize raw API/Network/Supabase errors to clean, precise user-friendly messages */
export function normalizeAuthError(err) {
  if (!err) return 'An unexpected error occurred. Please try again.';
  
  const code = (err.code || err.error_code || err.name || '').toString().toLowerCase();
  const status = err.status || err.statusCode || null;
  const message = (typeof err === 'string' ? err : err.message || err.error_description || '').toString();
  const lowerMsg = message.toLowerCase();

  // 1. Existing User / Duplicate Account
  if (
    code === 'user_already_exists' ||
    code === 'email_exists' ||
    lowerMsg.includes('already registered') ||
    lowerMsg.includes('user already registered') ||
    lowerMsg.includes('user_already_exists') ||
    lowerMsg.includes('already exists')
  ) {
    return 'An account with this email already exists. Please log in instead.';
  }

  // 2. Email Rate Limit (60-second cooldown per email address in Supabase Auth)
  if (
    code === 'over_email_send_rate_limit' ||
    lowerMsg.includes('over_email_send_rate_limit') ||
    lowerMsg.includes('once every 60 seconds') ||
    lowerMsg.includes('for security purposes, you can only request this once')
  ) {
    return 'A verification email was recently requested for this address. Please wait 60 seconds or check your inbox.';
  }

  // 3. General Request / IP Rate Limit (HTTP 429)
  if (
    status === 429 ||
    code === 'over_request_rate_limit' ||
    lowerMsg.includes('over_request_rate_limit') ||
    lowerMsg.includes('too many requests')
  ) {
    return 'Too many signup attempts from this device. Please wait a few minutes and try again.';
  }

  // 4. Auth Hook 404 or Hook Failures
  if (
    lowerMsg.includes('status code returned from hook: 404') ||
    lowerMsg.includes('returned from hook: 404') ||
    lowerMsg.includes('status code returned from hook') ||
    lowerMsg.includes('returned from hook') ||
    lowerMsg.includes('hook_error') ||
    lowerMsg.includes('hook_timeout')
  ) {
    return 'Authentication hook error (404): A configured Supabase Auth Hook returned an error. If you already signed in with Google, please complete your profile or log in with Google.';
  }

  // 5. Invalid Credentials / Not Authorized
  if (code === 'invalid_grant' || lowerMsg.includes('invalid login credentials') || lowerMsg.includes('invalid_grant')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  if (code === 'email_address_not_authorized' || lowerMsg.includes('not authorized')) {
    return 'This email address is not authorized for registration. Please contact support.';
  }

  if (code === 'email_address_invalid' || lowerMsg.includes('valid email') || lowerMsg.includes('invalid email') || lowerMsg.includes('unable to validate email')) {
    return 'Please enter a valid email address.';
  }

  // 6. Weak Password
  if (code === 'weak_password' || lowerMsg.includes('password should be at least') || lowerMsg.includes('password is too weak')) {
    return 'Password does not meet security requirements. Please choose a stronger password.';
  }

  // 7. Unconfirmed Email
  if (lowerMsg.includes('email not confirmed')) {
    return 'Please verify your email address before signing in. Check your inbox for a confirmation link.';
  }

  // 8. Connection / Network Errors
  if (
    lowerMsg.includes('failed to fetch') ||
    lowerMsg.includes('networkerror') ||
    lowerMsg.includes('fetch failed') ||
    lowerMsg.includes('network error')
  ) {
    return 'Unable to connect to authentication service. Please check your internet connection and try again.';
  }

  // 9. Provider Not Enabled
  if (lowerMsg.includes('provider is not enabled') || lowerMsg.includes('unsupported provider')) {
    return 'Google provider is not enabled in your Supabase project settings. Please enable Google in Supabase Dashboard -> Authentication -> Providers.';
  }

  // 10. Database / PostgREST single object coercion
  if (lowerMsg.includes('cannot coerce the result') || lowerMsg.includes('pgrst116')) {
    return 'Profile record not found or could not be retrieved. Please save your profile to create it.';
  }

  return message || 'Account creation could not be completed. Please try again.';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);
  const initRef = useRef(false);
  const signupInFlightRef = useRef(false);

  /** Fetch user profile from profiles table using maybeSingle() */
  const fetchProfile = useCallback(async (userId) => {
    if (!isSupabaseConfigured() || !userId) return null;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.warn('[AuthContext] Notice fetching profile:', error.message);
        return null;
      }
      return data || null;
    } catch (err) {
      console.warn('[AuthContext] Profile fetch exception:', err);
      return null;
    }
  }, []);

  /** Fetch creator roles from creator_roles table */
  const fetchRoles = useCallback(async (userId) => {
    if (!isSupabaseConfigured() || !userId) return [];
    try {
      const { data, error } = await supabase
        .from('creator_roles')
        .select('role, is_primary')
        .eq('user_id', userId);

      if (error) {
        console.warn('[AuthContext] Notice fetching roles:', error.message);
        return [];
      }
      return data ? data.map((r) => r.role) : [];
    } catch (err) {
      console.warn('[AuthContext] Roles fetch exception:', err);
      return [];
    }
  }, []);

  /** Load profile and roles for a user */
  const loadUserData = useCallback(async (authUser) => {
    if (!authUser) {
      setProfile(null);
      setRoles([]);
      return;
    }
    let [userProfile, userRoles] = await Promise.all([
      fetchProfile(authUser.id),
      fetchRoles(authUser.id),
    ]);

    // If profile is missing for an authenticated user (e.g. after Google OAuth or initial sign-in), auto-heal
    // BUT if the user's intent was to login, do NOT auto-create an artist profile (they must register first)
    const isLoginIntent = typeof window !== 'undefined' && sessionStorage.getItem('google_oauth_intent') === 'login';
    if (!userProfile && authUser.id && isSupabaseConfigured() && !isLoginIntent) {
      try {
        const meta = authUser.user_metadata || {};
        const metaName = meta.full_name || meta.name || meta.display_name || authUser.email?.split('@')[0] || '';
        const metaStage = meta.stage_name || metaName || 'Artist';
        const metaRole = meta.primary_role || null;
        const metaRoles = meta.additional_roles || [];

        const autoPayload = {
          id: authUser.id,
          email: authUser.email,
          name: metaName,
          stage_name: metaStage,
          role: 'user',
          total_streams: 0,
          total_releases: 0,
          is_verified: false,
          is_suspended: false,
          onboarding_completed: false,
          updated_at: new Date().toISOString(),
        };

        const { data: createdProf } = await supabase.from('profiles').upsert(autoPayload, { onConflict: 'id' }).select().maybeSingle();
        if (createdProf) {
          userProfile = createdProf;
        }

        const allMetaRoles = [metaRole, ...metaRoles].filter(Boolean);
        const uniqueMetaRoles = [...new Set(allMetaRoles)];
        if (uniqueMetaRoles.length > 0) {
          await supabase.from('creator_roles').delete().eq('user_id', authUser.id);
          const roleInserts = uniqueMetaRoles.map((r) => ({
            user_id: authUser.id,
            role: r,
            is_primary: r === metaRole,
          }));
          await supabase.from('creator_roles').insert(roleInserts);
          userRoles = uniqueMetaRoles;

          if (isArtistRole(uniqueMetaRoles)) {
            await supabase.from('artist_profiles').upsert({
              user_id: authUser.id,
              stage_name: metaStage,
              name: metaName,
              is_visible: true,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
          }
        }
      } catch (autoErr) {
        console.warn('[AuthContext] Auto-initialize profile notice:', autoErr.message);
      }
    }

    setProfile(userProfile);
    setRoles(userRoles);
  }, [fetchProfile, fetchRoles]);

  /** Create profile, roles, and artist profile safely and idempotently */
  const createProfile = useCallback(async (authUser, {
    fullName,
    legalName,
    stageName,
    phone,
    city,
    genre,
    secondary_genre,
    instagram,
    youtube,
    upi_id,
    spotify_link,
    avatar_url,
    primaryRole,
    additionalRoles,
    onboarding_completed = false,
  }) => {
    if (!isSupabaseConfigured() || !authUser) return null;
    try {
      const resolvedStageName = stageName || authUser.user_metadata?.stage_name || authUser.user_metadata?.display_name || authUser.user_metadata?.full_name || authUser.user_metadata?.name || 'Artist';
      const resolvedLegalName = legalName || authUser.user_metadata?.legal_name || null;
      const resolvedName = fullName || authUser.user_metadata?.full_name || authUser.user_metadata?.name || '';
      const resolvedCity = city || authUser.user_metadata?.city || null;
      const resolvedPhone = phone || authUser.user_metadata?.phone || null;
      const resolvedGenre = genre || authUser.user_metadata?.genre || null;
      const resolvedSecondaryGenre = secondary_genre || authUser.user_metadata?.secondary_genre || null;
      const resolvedInstagram = instagram || authUser.user_metadata?.instagram || null;
      const resolvedYoutube = youtube || authUser.user_metadata?.youtube || null;
      const resolvedUpiId = upi_id || authUser.user_metadata?.upi_id || null;
      const resolvedSpotifyLink = spotify_link || authUser.user_metadata?.spotify_link || null;
      const resolvedAvatarUrl = avatar_url || authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || (Array.isArray(authUser.user_metadata?.images) && authUser.user_metadata?.images[0]?.url) || null;
      // Do NOT default to 'singer' - only use role if explicitly provided or in OAuth metadata
      const resolvedPrimaryRole = primaryRole || authUser.user_metadata?.primary_role || null;
      const resolvedAdditionalRoles = additionalRoles || authUser.user_metadata?.additional_roles || [];

      // 1. Create / Upsert Profile record
      const profilePayload = {
        id: authUser.id,
        name: resolvedName,
        legal_name: resolvedLegalName,
        stage_name: resolvedStageName,
        email: authUser.email,
        phone: resolvedPhone,
        role: 'user',
        city: resolvedCity,
        genre: resolvedGenre,
        secondary_genre: resolvedSecondaryGenre,
        instagram: resolvedInstagram,
        youtube: resolvedYoutube,
        upi_id: resolvedUpiId,
        spotify_link: resolvedSpotifyLink,
        total_streams: 0,
        total_releases: 0,
        is_verified: false,
        is_suspended: false,
        onboarding_completed: !!onboarding_completed,
        updated_at: new Date().toISOString(),
      };

      if (resolvedAvatarUrl) {
        profilePayload.avatar_url = resolvedAvatarUrl;
      }

      const { data: profileData, error: profileError } = await supabase.from('profiles').upsert(
        profilePayload,
        { onConflict: 'id' }
      ).select().maybeSingle();

      if (profileError) {
        console.error('[AuthContext] Profile upsert error:', profileError.message);
        throw profileError;
      }

      // 2. Reconcile Creator Roles (delete existing to prevent duplicate/stale roles, then insert)
      const allRoles = [resolvedPrimaryRole, ...resolvedAdditionalRoles].filter(Boolean);
      const uniqueRoles = [...new Set(allRoles)];

      if (uniqueRoles.length > 0) {
        const { error: deleteError } = await supabase.from('creator_roles').delete().eq('user_id', authUser.id);
        if (deleteError) {
          console.error('[AuthContext] Creator role delete error:', deleteError.message);
          throw deleteError;
        }

        const roleInserts = uniqueRoles.map((r) => ({
          user_id: authUser.id,
          role: r,
          is_primary: r === resolvedPrimaryRole,
        }));

        const { error: roleError } = await supabase.from('creator_roles').insert(roleInserts);
        if (roleError) {
          console.error('[AuthContext] Creator role insert error:', roleError.message);
          throw roleError;
        }
      }

      // 3. Create / Upsert Artist Profile if role qualifies (singer, rapper, producer)
      if (uniqueRoles.length > 0 && isArtistRole(uniqueRoles)) {
        const artistPayload = {
          user_id: authUser.id,
          stage_name: resolvedStageName,
          name: resolvedName,
          city: resolvedCity,
          genre: resolvedGenre,
          secondary_genre: resolvedSecondaryGenre,
          spotify_link: resolvedSpotifyLink,
          instagram: resolvedInstagram,
          youtube: resolvedYoutube,
          is_visible: true,
          updated_at: new Date().toISOString(),
        };

        if (resolvedAvatarUrl) {
          artistPayload.avatar_url = resolvedAvatarUrl;
        }

        const { error: artistError } = await supabase.from('artist_profiles').upsert(
          artistPayload,
          { onConflict: 'user_id' }
        );

        if (artistError) {
          console.error('[AuthContext] Artist profile upsert error:', artistError.message);
          throw artistError;
        }

        // Verify that artist_profiles exists before treating onboarding as complete
        const { data: verifiedArtist, error: verifyError } = await supabase
          .from('artist_profiles')
          .select('id, user_id, stage_name, is_visible')
          .eq('user_id', authUser.id)
          .maybeSingle();

        if (verifyError || !verifiedArtist) {
          const verifyMessage = verifyError?.message || 'Verification of artist profile in database failed.';
          console.error('[AuthContext] Artist profile verification failed:', verifyMessage);
          throw new Error(verifyMessage);
        }
      }

      // Refresh loaded user data
      await loadUserData(authUser);
      return profileData;
    } catch (err) {
      console.error('[AuthContext] createProfile failed:', err);
      throw err;
    }
  }, [loadUserData]);

  /** Initialize auth state on mount */
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    if (!isSupabaseConfigured()) {
      setLoading(false);
      setSessionChecked(true);
      return;
    }

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          await loadUserData(session.user);
        } else if (typeof window !== 'undefined' && sessionStorage.getItem('senritsux_dev_user')) {
          try {
            const devUser = JSON.parse(sessionStorage.getItem('senritsux_dev_user'));
            if (devUser?.id) {
              setUser(devUser);
              await loadUserData(devUser);
            }
          } catch {}
        }
      } catch (err) {
        console.error('[AuthContext] Session restoration error:', err);
      } finally {
        setLoading(false);
        setSessionChecked(true);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          setUser(session.user);
          setLoading(false);
          setSessionChecked(true);

          setTimeout(() => {
            loadUserData(session.user);
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setRoles([]);
          setLoading(false);
          setSessionChecked(true);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUser(session.user);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, [loadUserData]);

  /** Sign in with email + password */
  const signIn = async (email, password) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set your project credentials in frontend/.env.');
    }
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.user) {
        setUser(data.user);
        await loadUserData(data.user);
      }
      return data;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Sign up with email + password + metadata (or complete profile for existing authenticated users) */
  const signUp = async ({
    email,
    password,
    fullName,
    stageName,
    phone,
    city,
    primaryRole,
    additionalRoles,
    onboarding_completed = true,
  }) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set your project credentials in frontend/.env.');
    }

    // Prevent concurrent duplicate signup requests
    if (signupInFlightRef.current) {
      console.warn('[AuthContext] Signup request already in-flight. Ignoring concurrent call.');
      throw new Error('A signup request is already in progress. Please wait.');
    }

    signupInFlightRef.current = true;

    // Development logging (safe email masking, zero password logging)
    const maskedEmail = email ? email.replace(/(?<=.{2}).(?=.*@)/g, '*') : 'unknown';
    console.log('[Auth] Signup initiated for email:', maskedEmail);

    try {
      // If user is already authenticated (e.g. from Google OAuth), DO NOT call supabase.auth.signUp()
      if (user && user.id) {
        console.log('[Auth] Active session detected during signUp. Completing profile for existing user ID:', user.id);
        await createProfile(user, {
          fullName: fullName || user.user_metadata?.full_name,
          stageName: stageName || user.user_metadata?.stage_name,
          phone,
          city,
          primaryRole,
          additionalRoles,
          onboarding_completed: true,
        });
        await completeOnboarding();
        return { user, session: null };
      }

      // Strong password policy enforcement at auth layer
      const pwdValidation = validatePassword(password, { email, fullName, stageName });
      if (!pwdValidation.isValid) {
        throw new Error(pwdValidation.error || 'Password does not meet security requirements.');
      }

      console.log('[Auth] Dispatching single-flight supabase.auth.signUp request...');
      const redirectUrl = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '/auth/callback';
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            stage_name: stageName,
            phone,
            city,
            primary_role: primaryRole,
            additional_roles: additionalRoles || [],
            onboarding_completed,
          },
        },
      });

      if (error) {
        console.warn('[Auth] Supabase signup error code:', error.code || error.name, 'message:', error.message);
        throw error;
      }

      console.log('[Auth] Supabase signup succeeded. Auth User ID:', data?.user?.id || 'pending-confirmation');

      // If instant session is established (email confirmation disabled in Supabase)
      if (data.user && data.session) {
        console.log('[Auth] Instant session established. Initializing profile & roles...');
        await createProfile(data.user, {
          fullName,
          stageName,
          phone,
          city,
          primaryRole,
          additionalRoles,
          onboarding_completed: true,
        });
      }

      return data;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    } finally {
      signupInFlightRef.current = false;
    }
  };

  /** Sign in with Google OAuth */
  const signInWithGoogle = async () => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set your project credentials in frontend/.env to enable Google Sign-In.');
    }
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (error) throw error;
      return data;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Sign in with Spotify OAuth */
  const signInWithSpotify = async () => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set your project credentials in frontend/.env to enable Spotify Sign-In.');
    }
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'spotify',
        options: {
          redirectTo: redirectUrl,
          scopes: 'user-read-email user-read-private',
        },
      });
      if (error) throw error;
      return data;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Sign out */
  const signOut = async () => {
    if (isSupabaseConfigured()) {
      try {
        await supabase.auth.signOut();
      } catch (err) {
        console.error('[AuthContext] SignOut error:', err);
      }
    }
    setUser(null);
    setProfile(null);
    setRoles([]);
  };

  /** Send password reset email */
  const resetPassword = async (email) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set your project credentials in frontend/.env.');
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Update password */
  const updatePassword = async (newPassword) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set your project credentials in frontend/.env.');
    }

    // Strong password policy enforcement at auth layer
    const userContext = {
      email: user?.email || '',
      fullName: profile?.name || user?.user_metadata?.full_name || '',
      stageName: profile?.stage_name || user?.user_metadata?.stage_name || '',
    };
    const pwdValidation = validatePassword(newPassword, userContext);
    if (!pwdValidation.isValid) {
      throw new Error(pwdValidation.error || 'Password does not meet security requirements.');
    }

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Resend confirmation email */
  const resendConfirmation = async (email) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set your project credentials in frontend/.env.');
    }
    try {
      const redirectUrl = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '/auth/callback';
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });
      if (error) throw error;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Update profile data atomically and safely */
  const updateProfile = async (updates) => {
    if (!user) throw new Error('Not authenticated. Please sign in.');
    if (!isSupabaseConfigured()) throw new Error('Supabase is not configured.');

    try {
      // 1. Prepare clean profile payload
      const profilePayload = {
        id: user.id,
        email: user.email,
        ...updates,
        updated_at: new Date().toISOString(),
      };

      // 2. Upsert profile safely using maybeSingle()
      let { data: updatedData, error: updateError } = await supabase
        .from('profiles')
        .upsert(profilePayload, { onConflict: 'id' })
        .select()
        .maybeSingle();

      if (updateError && (updateError.code === '42703' || updateError.code === 'PGRST204')) {
        const safePayload = { ...profilePayload };
        delete safePayload.artist_web_about;
        delete safePayload.apple_music_link;
        delete safePayload.youtube_music_link;
        const res = await supabase
          .from('profiles')
          .upsert(safePayload, { onConflict: 'id' })
          .select()
          .maybeSingle();
        updatedData = res.data;
        updateError = res.error;
      }

      if (updateError) {
        console.error('[AuthContext] Error updating profile:', updateError);
        throw updateError;
      }

      const resolvedProfile = updatedData || { ...profile, ...updates };
      setProfile(resolvedProfile);

      // 3. Sync to artist_profiles table if user has an artist role or stage_name
      const shouldSyncArtist = isArtistRole(roles) || updates.stage_name || updates.genre || updates.secondary_genre !== undefined || updates.artist_web_about || updates.instagram !== undefined;
      if (shouldSyncArtist) {
        const artistUpdates = {
          user_id: user.id,
          stage_name: updates.stage_name || resolvedProfile.stage_name || 'Artist',
          name: updates.name !== undefined ? updates.name : resolvedProfile.name || null,
          genre: updates.genre !== undefined ? updates.genre : resolvedProfile.genre || null,
          secondary_genre: updates.secondary_genre !== undefined ? updates.secondary_genre : resolvedProfile.secondary_genre || null,
          city: updates.city !== undefined ? updates.city : resolvedProfile.city || null,
          avatar_url: updates.avatar_url !== undefined ? updates.avatar_url : resolvedProfile.avatar_url || null,
          spotify_link: updates.spotify_link !== undefined ? updates.spotify_link : resolvedProfile.spotify_link || null,
          apple_music_link: updates.apple_music_link !== undefined ? updates.apple_music_link : resolvedProfile.apple_music_link || null,
          youtube_music_link: updates.youtube_music_link !== undefined ? updates.youtube_music_link : resolvedProfile.youtube_music_link || null,
          instagram: updates.instagram !== undefined ? updates.instagram : resolvedProfile.instagram || null,
          youtube: updates.youtube !== undefined ? updates.youtube : resolvedProfile.youtube || null,
          artist_web_about: updates.artist_web_about !== undefined ? (updates.artist_web_about ? updates.artist_web_about.trim().slice(0, 150) : null) : resolvedProfile.artist_web_about || null,
          is_visible: true,
          updated_at: new Date().toISOString(),
        };

        let { error: artistError } = await supabase
          .from('artist_profiles')
          .upsert(artistUpdates, { onConflict: 'user_id' });

        if (artistError && (artistError.code === '42703' || artistError.code === 'PGRST204')) {
          const safeArtistUpdates = { ...artistUpdates };
          delete safeArtistUpdates.artist_web_about;
          delete safeArtistUpdates.apple_music_link;
          delete safeArtistUpdates.youtube_music_link;
          delete safeArtistUpdates.secondary_genre;
          const res = await supabase
            .from('artist_profiles')
            .upsert(safeArtistUpdates, { onConflict: 'user_id' });
          artistError = res.error;
        }

        if (artistError) {
          console.error('[AuthContext] Artist profile sync error:', artistError.message);
          throw artistError;
        }
      }

      return resolvedProfile;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Update creator roles and refresh permission-based access */
  const updateRoles = async (newRoles, primaryRole) => {
    if (!user) throw new Error('You must be logged in to update roles.');
    if (!Array.isArray(newRoles) || newRoles.length === 0) {
      throw new Error('Please select at least one role.');
    }

    const uniqueRoles = [...new Set(newRoles.filter(Boolean))];
    const resolvedPrimary = primaryRole && uniqueRoles.includes(primaryRole) ? primaryRole : uniqueRoles[0];

    try {
      if (isSupabaseConfigured()) {
        // 1. Delete previous creator roles for this user
        const { error: deleteError } = await supabase
          .from('creator_roles')
          .delete()
          .eq('user_id', user.id);

        if (deleteError) {
          console.error('[AuthContext] Role delete error:', deleteError.message);
          throw deleteError;
        }

        // 2. Insert new creator roles
        const roleInserts = uniqueRoles.map((r) => ({
          user_id: user.id,
          role: r,
          is_primary: r === resolvedPrimary,
        }));

        const { error: insertError } = await supabase
          .from('creator_roles')
          .insert(roleInserts);

        if (insertError) {
          console.error('[AuthContext] Role insert error:', insertError);
          throw insertError;
        }

        // 3. Ensure artist profile exists if any role qualifies
        if (isArtistRole(uniqueRoles)) {
          const { error: artistError } = await supabase
            .from('artist_profiles')
            .upsert({
              user_id: user.id,
              stage_name: profile?.stage_name || user.user_metadata?.stage_name || profile?.name || 'Artist',
              name: profile?.name || user.user_metadata?.full_name || null,
              genre: profile?.genre || null,
              secondary_genre: profile?.secondary_genre || null,
              city: profile?.city || null,
              avatar_url: profile?.avatar_url || null,
              spotify_link: profile?.spotify_link || null,
              instagram: profile?.instagram || null,
              youtube: profile?.youtube || null,
              is_visible: true,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });

          if (artistError) {
            console.error('[AuthContext] Artist profile sync error:', artistError.message);
            throw artistError;
          }
        }
      }

      // 4. Update local state immediately for instant responsive navigation/permission updates
      setRoles(uniqueRoles);
      return uniqueRoles;
    } catch (err) {
      throw new Error(normalizeAuthError(err));
    }
  };

  /** Mark onboarding as completed */
  const completeOnboarding = async () => {
    if (!user) return;
    await updateProfile({ onboarding_completed: true });
  };

  /** Complete full onboarding lifecycle atomically with all collected user data */
  const completeOnboardingWithData = async (data = {}) => {
    if (!user) throw new Error('Not authenticated.');
    if (!isSupabaseConfigured()) throw new Error('Database is not configured.');

    const fullName = data.fullName?.trim() || profile?.name || user.user_metadata?.full_name || '';
    const legalName = data.legalName?.trim() || profile?.legal_name || null;
    const stageName = data.stageName?.trim() || profile?.stage_name || fullName || 'Artist';
    const city = data.city?.trim() || profile?.city || null;
    const avatarUrl = data.avatarUrl || profile?.avatar_url || null;
    const primaryRole = data.primaryRole || (roles && roles[0]) || 'singer';
    const additionalRoles = Array.isArray(data.additionalRoles) ? data.additionalRoles : [];
    const rawIg = data.instagram !== undefined ? data.instagram : profile?.instagram;
    const normalizedIg = rawIg ? normalizeInstagramUsername(rawIg) : null;
    const genre = data.genre || profile?.genre || null;
    const secondaryGenre = data.secondaryGenre || profile?.secondary_genre || null;
    const artistWebAbout = data.artistWebAbout !== undefined ? data.artistWebAbout : (profile?.artist_web_about || null);
    const spotifyLink = data.spotifyLink || profile?.spotify_link || null;
    const appleMusicLink = data.appleMusicLink || profile?.apple_music_link || null;
    const youtubeMusicLink = data.youtubeMusicLink || profile?.youtube_music_link || null;
    const youtube = data.youtube || profile?.youtube || null;

    const allRoles = [primaryRole, ...additionalRoles].filter(Boolean);
    const uniqueRoles = [...new Set(allRoles)];

    // 1. Attempt atomic RPC complete_user_onboarding if migration 024/025 is installed
    let rpcSucceeded = false;
    try {
      const { data: rpcRes, error: rpcErr } = await supabase.rpc('complete_user_onboarding', {
        p_name: fullName,
        p_stage_name: stageName,
        p_legal_name: legalName,
        p_phone: data.phone || profile?.phone || null,
        p_city: city,
        p_genre: genre,
        p_instagram: normalizedIg,
        p_avatar_url: avatarUrl,
        p_artist_web_about: artistWebAbout,
        p_spotify_link: spotifyLink,
        p_apple_music_link: appleMusicLink,
        p_youtube_music_link: youtubeMusicLink,
        p_youtube: youtube,
        p_upi_id: data.upiId || profile?.upi_id || null,
        p_primary_role: primaryRole,
        p_roles: uniqueRoles,
        p_secondary_genre: secondaryGenre,
      });

      if (!rpcErr && rpcRes && rpcRes.success) {
        rpcSucceeded = true;
      }
    } catch (rpcEx) {
      console.warn('[AuthContext] Notice: atomic RPC complete_user_onboarding unavailable, running client fallback:', rpcEx);
    }

    // 2. Client-side multi-table fallback if RPC is not installed yet
    if (!rpcSucceeded) {
      // Step A: Upsert profile
      const profPayload = {
        id: user.id,
        email: user.email,
        name: fullName,
        legal_name: legalName,
        stage_name: stageName,
        phone: data.phone || profile?.phone || null,
        city,
        avatar_url: avatarUrl,
        instagram: normalizedIg,
        genre,
        secondary_genre: secondaryGenre,
        artist_web_about: artistWebAbout,
        spotify_link: spotifyLink,
        apple_music_link: appleMusicLink,
        youtube_music_link: youtubeMusicLink,
        youtube,
        upi_id: data.upiId || profile?.upi_id || null,
        onboarding_completed: true,
        updated_at: new Date().toISOString(),
      };

      let { error: pErr } = await supabase.from('profiles').upsert(profPayload, { onConflict: 'id' });
      if (pErr && (pErr.code === '42703' || pErr.code === 'PGRST204')) {
        const safeP = { ...profPayload };
        delete safeP.artist_web_about;
        delete safeP.apple_music_link;
        delete safeP.youtube_music_link;
        delete safeP.secondary_genre;
        const resP = await supabase.from('profiles').upsert(safeP, { onConflict: 'id' });
        pErr = resP.error;
      }
      if (pErr) throw pErr;

      // Step B: Reconcile creator roles
      if (uniqueRoles.length > 0) {
        await supabase.from('creator_roles').delete().eq('user_id', user.id);
        const roleInserts = uniqueRoles.map((r) => ({
          user_id: user.id,
          role: r,
          is_primary: r === primaryRole,
        }));
        await supabase.from('creator_roles').insert(roleInserts);
        setRoles(uniqueRoles);
      }

      // Step C: Upsert artist_profiles record ONLY if role qualifies as an artist (singer, rapper, producer)
      if (isArtistRole(uniqueRoles)) {
        const artistPayload = {
          user_id: user.id,
          stage_name: stageName,
          name: fullName,
          city,
          genre,
          secondary_genre: secondaryGenre,
          avatar_url: avatarUrl,
          instagram: normalizedIg,
          artist_web_about: artistWebAbout,
          spotify_link: spotifyLink,
          apple_music_link: appleMusicLink,
          youtube_music_link: youtubeMusicLink,
          youtube,
          is_visible: true,
          updated_at: new Date().toISOString(),
        };

        let { error: aErr } = await supabase.from('artist_profiles').upsert(artistPayload, { onConflict: 'user_id' });
        if (aErr && (aErr.code === '42703' || aErr.code === 'PGRST204')) {
          const safeA = { ...artistPayload };
          delete safeA.artist_web_about;
          delete safeA.apple_music_link;
          delete safeA.youtube_music_link;
          delete safeA.secondary_genre;
          const resA = await supabase.from('artist_profiles').upsert(safeA, { onConflict: 'user_id' });
          aErr = resA.error;
        }
        if (aErr) console.warn('[AuthContext] Notice upserting artist_profiles:', aErr.message);
      }
    }

    // Refresh context state
    await loadUserData(user);
    setProfile((prev) => ({
      ...(prev || {}),
      name: fullName,
      stage_name: stageName,
      city,
      avatar_url: avatarUrl,
      instagram: normalizedIg,
      genre,
      secondary_genre: secondaryGenre,
      artist_web_about: artistWebAbout,
      onboarding_completed: true,
    }));
  };

  /** Refresh profile and roles */
  const refreshUserData = async () => {
    if (user) {
      await loadUserData(user);
    }
  };

  /** Check if an email is already registered in profiles */
  const checkEmailExists = async (emailToCheck) => {
    if (!isSupabaseConfigured() || !emailToCheck) return false;
    try {
      const trimmed = emailToCheck.trim().toLowerCase();
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .ilike('email', trimmed)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.warn('[AuthContext] checkEmailExists notice:', error.message);
        return false;
      }
      return !!data;
    } catch (err) {
      console.warn('[AuthContext] checkEmailExists exception:', err);
      return false;
    }
  };

  const value = {
    user,
    profile,
    roles,
    loading,
    sessionChecked,
    isAuthenticated: !!user,
    isConfigured: isSupabaseConfigured(),
    signIn,
    signUp,
    checkEmailExists,
    signInWithGoogle,
    signInWithSpotify,
    signOut,
    resetPassword,
    updatePassword,
    resendConfirmation,
    updateProfile,
    updateRoles,
    completeOnboarding,
    completeOnboardingWithData,
    createProfile,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
